/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import useAbortController from './useAbortController';
import axiosCall from '../../services/axios';
import { useAuthContext } from '../../contexts/AuthContext';
import { useLoanContext } from '../../contexts/LoanContext';

type BillingCycleHookData = {
  billingCycleData: BillingCycleData;
  loadingBillingCycle: boolean;
};

const useBillingCycle = (): BillingCycleHookData => {
  const { get, reset } = useAbortController();
  const queryClient = useQueryClient();
  const { token } = useAuthContext();
  const { loanNumber } = useLoanContext();
  const queryKey = useMemo(() => ['billingCycle', loanNumber, token], [loanNumber, token]);

  // Fetch communication data using react-query
  const { data, isFetching, isLoading } = useQuery({
    queryKey: queryKey,
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      if (!loanNumber || !token) return { data: null };
      try {
        const request = {
          url: `live/master/company/SUNWST000/preferences`,
          method: 'post' as const,
          data: { name: 'HELOC_CUTOFF_DOM' },
        };
        const config = reset();
        const headers = {
          'Session-Token': token,
        };
        const response = await axiosCall(request, config, headers);
        if (!response) throw new Error(`No billing cycle returned for HELOC_CUTOFF_DOM`);

        const normalized: BillingCycleData = {
          isLoading: false,
          name: response.name ?? 'HELOC_CUTOFF_DOM',
          value: response.value ?? '',
        };
        return { data: normalized };
      } catch (error) {
        return { data: null };
      }
    },
  });

  return {
    loadingBillingCycle: isFetching || isLoading,
    billingCycleData: data?.data ? data.data : null,
  };
};

export default useBillingCycle;
